import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { IHabitationalPolicyChange } from "./habitationalPolicyChangeReq.types";
import { makeApiCall } from "../../../helpers/postRequest";
import { IDatatableV2AdvancedSearchFilter } from "../../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { uploadTaskFile } from "../../tasks/tasksActions";
import { IInternalLogEntry } from "../../accessLogs/accessLogs.types";
import {
  EDIT_POLICY_CHANGE_REQUEST_V2,
  EDIT_PUBLIC_POLICY_CHANGE_REQUEST_V2,
  GET_POLICY_CHANGE_REQUEST_V2,
  POLICY_CHANGE_V2_TYPE,
  POLICY_CHANGE_V2_GET_TYPE,
  buildPolicyChangeV2Query,
  omitPolicyChangeV2Fields,
} from "../../../constants/policyChangeV2.constants";

export const FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_PROGRESS =
  "FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_PROGRESS";
export const FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_SUCCESS =
  "FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_SUCCESS";
export const FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_FAILED =
  "FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_FAILED";

export const fetchHabitationalPolicyChangeReqListProgress = () =>
  action(FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_PROGRESS);
export const fetchHabitationalPolicyChangeReqListSuccess = (
  list: IHabitationalPolicyChange[],
  totalRecords: number
) =>
  action(FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_SUCCESS, {
    list,
    totalRecords,
  });
export const fetchHabitationalPolicyChangeReqListFailed = () =>
  action(FETCH_HABITATIONAL_POLICY_CHANGE_REQ_LIST_FAILED);

export const fetchHabitationalPolicyChangeListAsync =
  (
    customerpolicyId: number,
    status: string,
    searchValue: IDatatableV2AdvancedSearchFilter,
    date: IDataTableV2DateState["dates"]
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchHabitationalPolicyChangeReqListProgress());

      let finalUrl =
        GET_POLICY_CHANGE_REQUEST_V2 +
        buildPolicyChangeV2Query({
          policy_type: POLICY_CHANGE_V2_GET_TYPE.HABITATIONAL,
          customer_policy_id: customerpolicyId,
          from_date: date.fromDate,
          to_date: date.toDate,
        });

      if (status !== undefined && status !== "-1") {
        finalUrl += "&status=" + status;
      }
      if (searchValue.length > 0) {
        finalUrl += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      const res = await api.get(finalUrl);
      const data: IHabitationalPolicyChange[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchHabitationalPolicyChangeReqListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchHabitationalPolicyChangeReqListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_PROGRESS =
  "FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_PROGRESS";
export const FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_SUCCESSS =
  "FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_SUCCESSS";
export const FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_FAILED =
  "FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_FAILED";

export const fetchHabitationalPolicyChangeReqProgress = () =>
  action(FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_PROGRESS);
export const fetchHabitationalPolicyChangeReqSuccess = (
  data: IHabitationalPolicyChange
) =>
  action(FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_SUCCESSS, { data });
export const fetchHabitationalPolicyChangeReqFailed = (error?: string) =>
  action(FETCH_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_FAILED, { error });

export const fetchHabitationalPolicyChangeReqAsync =
  (
    customerPolicyId: number,
    incrementalNumber: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchHabitationalPolicyChangeReqProgress());
      const res = await api.get(
        `/policy/get-habitational-policy-change-request?customer_policy_id=${customerPolicyId}&change_request_code=${incrementalNumber}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchHabitationalPolicyChangeReqSuccess({ ...data[0] }));
      } else {
        dispatch(
          fetchHabitationalPolicyChangeReqFailed(ERROR_MESSAGES.NO_RECORD_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(fetchHabitationalPolicyChangeReqFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addHabitationalPolicyChange =
  (
    data: IHabitationalPolicyChange,
    initialData: IHabitationalPolicyChange,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, data?: IHabitationalPolicyChange) => void,
    logData?: IInternalLogEntry,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        policy_type: data.policy_type,
        customer_policy_id: data.customer_policy_id,
        page_name: "Habitational Policy Change",
      };

      const updateFilesJson = await uploadTaskFile(
        data.attachments as any,
        "POLICY",
        asPayload
      );

      const res = await makeApiCall(
        {
          url: "/policy/edit-habitational-policy-change-request",
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "change_request_code",
          },
        },
        initialData,
        {
          ...data,
          attachments: updateFilesJson,
        }
      );

      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Policy Changed successfully!",
          displayAs: "snackbar",
        })
      );
      dispatch(fetchHabitationalPolicyChangeReqSuccess(data));
      if (logData) {
        dispatch({ type: "custom/log", logData });
      }
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_STATE =
  "CLEAR_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_STATE";

export const clearHabitationalPolicyChangeReqState = () =>
  action(CLEAR_HABITATIONAL_POLICY_CHANGE_REQ_APPLICATION_STATE);

// ==========================================================================
// v2: consolidated policy-change-request endpoints
// POST /policy/edit-policy-change-request  (discriminates by policy_type)
// GET  /policy/get-policy-change-request   (filtered list)
// ==========================================================================

export const fetchHabitationalPolicyChangeReqV2Async =
  (
    customerPolicyId: number,
    incrementalNumber: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchHabitationalPolicyChangeReqProgress());
      const finalUrl =
        GET_POLICY_CHANGE_REQUEST_V2 +
        buildPolicyChangeV2Query({
          policy_type: POLICY_CHANGE_V2_GET_TYPE.HABITATIONAL,
          customer_policy_id: customerPolicyId,
          change_request_code: incrementalNumber,
          limit: 1,
        });
      const res = await api.get(finalUrl);
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchHabitationalPolicyChangeReqSuccess({ ...data[0] }));
      } else {
        dispatch(
          fetchHabitationalPolicyChangeReqFailed(ERROR_MESSAGES.NO_RECORD_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(fetchHabitationalPolicyChangeReqFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addHabitationalPolicyChangeV2 =
  (
    data: IHabitationalPolicyChange,
    initialData: IHabitationalPolicyChange,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, data?: IHabitationalPolicyChange) => void,
    logData?: IInternalLogEntry,
    isPublic?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        policy_type: data.policy_type,
        customer_policy_id: data.customer_policy_id,
        page_name: "Habitational Policy Change",
      };

      const updateFilesJson = await uploadTaskFile(
        data.attachments as any,
        "POLICY",
        asPayload
      );

      const res = await makeApiCall(
        {
          url: isPublic
            ? EDIT_PUBLIC_POLICY_CHANGE_REQUEST_V2
            : EDIT_POLICY_CHANGE_REQUEST_V2,
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "change_request_code",
          },
        },
        omitPolicyChangeV2Fields(initialData),
        {
          ...omitPolicyChangeV2Fields(data),
          policy_type: POLICY_CHANGE_V2_TYPE.HABITATIONAL,
          attachments: updateFilesJson,
        },
        isPublic
      );

      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Policy Changed successfully!",
          displayAs: "snackbar",
        })
      );
      dispatch(fetchHabitationalPolicyChangeReqSuccess(data));
      if (logData) {
        dispatch({ type: "custom/log", logData });
      }
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
